import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { gamesAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const EMPTY = {
  mode: 'points',
  variant: '101',
  point_value: 1,
  entry_fee: 80,
  max_players: 6,
  title: '',
  is_active: true,
  sort_order: 10,
};

const modeLabel = (mode, variant) => {
  if (mode === 'pool') return `Pool ${variant || ''}`;
  if (mode === 'deals') return `Deals · best of ${variant || ''}`;
  return 'Points';
};

export default function GameLobbyPanel() {
  const [modes, setModes] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await gamesAPI.getLobby();
      setModes(res.data.modes || []);
      setTables(res.data.tables || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load lobby tables');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visible = useMemo(
    () => tables.filter((row) => modeFilter === 'all' || row.mode === modeFilter),
    [tables, modeFilter],
  );

  const saveMode = async (mode, patch) => {
    try {
      await gamesAPI.updateLobbyMode(mode, patch);
      setModes((prev) => prev.map((row) => (row.mode === mode ? { ...row, ...patch } : row)));
      toast.success('Mode updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update mode');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, sort_order: tables.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      mode: row.mode,
      variant: row.variant ? String(row.variant) : '101',
      point_value: row.point_value ?? 0,
      entry_fee: row.entry_fee ?? 0,
      max_players: row.max_players || 6,
      title: row.title || '',
      is_active: row.is_active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialogOpen(true);
  };

  const saveTable = async () => {
    const payload = {
      ...form,
      variant: form.mode === 'points' ? null : Number(form.variant),
      point_value: Number(form.point_value) || 0,
      entry_fee: Number(form.entry_fee),
      max_players: Number(form.max_players),
      sort_order: Number(form.sort_order) || 0,
    };
    if (Number.isNaN(payload.entry_fee) || payload.entry_fee < 0) {
      toast.error('Enter a valid entry fee');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await gamesAPI.updateLobbyTable(editing.id, payload);
        toast.success('Table updated');
      } else {
        await gamesAPI.createLobbyTable(payload);
        toast.success('Table created');
      }
      setDialogOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save table');
    } finally {
      setSaving(false);
    }
  };

  const removeTable = async (row) => {
    if (!window.confirm('Remove this table from the app?')) return;
    try {
      await gamesAPI.deleteLobbyTable(row.id);
      toast.success('Table removed');
      setTables((prev) => prev.filter((item) => item.id !== row.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete table');
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {modes.map((mode) => (
          <div key={mode.mode} className="stat-card space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="font-medium capitalize">{mode.mode}</p>
              <Switch
                checked={mode.enabled !== false}
                onCheckedChange={(enabled) => saveMode(mode.mode, { enabled })}
              />
            </div>
            <Input
              value={mode.title || ''}
              onChange={(e) => setModes((prev) => prev.map((row) => (row.mode === mode.mode ? { ...row, title: e.target.value } : row)))}
            />
            <Input
              value={mode.subtitle || ''}
              onChange={(e) => setModes((prev) => prev.map((row) => (row.mode === mode.mode ? { ...row, subtitle: e.target.value } : row)))}
            />
            <Button variant="outline" size="sm" onClick={() => saveMode(mode.mode, { title: mode.title, subtitle: mode.subtitle })}>
              Save mode
            </Button>
            <p className="text-xs text-muted-foreground">
              {mode.enabled === false ? 'Hidden in the app' : 'Visible in the app'}
            </p>
          </div>
        ))}
      </div>

      <div className="data-table-container">
        <div className="data-table-header">
          <div>
            <h3 className="data-table-title">Tables in the app ({visible.length})</h3>
            <p className="text-sm text-muted-foreground">These rows are the stake list players see for each mode.</p>
          </div>
          <div className="filter-bar">
            <Select value={modeFilter} onValueChange={setModeFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="points">Points</SelectItem>
                <SelectItem value="pool">Pool</SelectItem>
                <SelectItem value="deals">Deals</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={openCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Add table
            </Button>
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="table-header">
              <TableHead>Mode</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="text-right">Point value</TableHead>
              <TableHead className="text-right">Entry fee</TableHead>
              <TableHead>Players</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>In app</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                  No tables for this mode
                </TableCell>
              </TableRow>
            ) : visible.map((row) => (
              <TableRow key={row.id} className="table-row">
                <TableCell>{modeLabel(row.mode, row.variant)}</TableCell>
                <TableCell>{row.title || '—'}</TableCell>
                <TableCell className="text-right font-mono">
                  {row.mode === 'points' ? `₹${row.point_value}` : '—'}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {Number(row.entry_fee) <= 0 ? 'Free' : formatCurrency(row.entry_fee)}
                </TableCell>
                <TableCell>{row.max_players}</TableCell>
                <TableCell>{row.sort_order}</TableCell>
                <TableCell>
                  <Switch
                    checked={row.is_active !== false}
                    onCheckedChange={async (is_active) => {
                      try {
                        await gamesAPI.updateLobbyTable(row.id, { ...row, is_active });
                        setTables((prev) => prev.map((item) => (item.id === row.id ? { ...item, is_active } : item)));
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to update table');
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(row)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => removeTable(row)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit table' : 'Add table'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(mode) => setForm({ ...form, mode, max_players: mode === 'deals' ? 2 : form.max_players })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="points">Points</SelectItem>
                  <SelectItem value="pool">Pool</SelectItem>
                  <SelectItem value="deals">Deals</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.mode !== 'points' ? (
              <div className="space-y-2">
                <Label>{form.mode === 'pool' ? 'Pool' : 'Deals'}</Label>
                <Select value={String(form.variant)} onValueChange={(variant) => setForm({ ...form, variant })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {form.mode === 'pool' ? (
                      <>
                        <SelectItem value="101">101</SelectItem>
                        <SelectItem value="201">201</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="2">Best of 2</SelectItem>
                        <SelectItem value="3">Best of 3</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Point value (₹ per point)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.point_value}
                  onChange={(e) => {
                    const point_value = parseFloat(e.target.value);
                    setForm({
                      ...form,
                      point_value,
                      entry_fee: Number.isNaN(point_value) ? form.entry_fee : Math.round(point_value * 80 * 100) / 100,
                    });
                  }}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Entry fee (0 = free)</Label>
              <Input
                type="number"
                min="0"
                value={form.entry_fee}
                onChange={(e) => setForm({ ...form, entry_fee: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Players</Label>
              <Select value={String(form.max_players)} onValueChange={(value) => setForm({ ...form, max_players: parseInt(value, 10) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 players</SelectItem>
                  <SelectItem value="6">6 players</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Title (optional)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show in the app</Label>
              <Switch checked={form.is_active} onCheckedChange={(is_active) => setForm({ ...form, is_active })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTable} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
